import { HookFormTextInput } from '@realtyka/real-ui';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Control,
  FieldErrors,
  useForm,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyPromoCode,
  checkAndApplyInvitationCoupon,
  clearPromoCode,
  InvitationType,
} from '../../slices/PaymentSlice';
import { AppDispatch, RootState } from '../../types';
import OneTimeFeeCard, { FeeItem } from '../OneTimeFeeCard';
import CardCvcInput from './CardCvcInput';
import CardExpiryInput from './CardExpiryInput';
import CardNumberInput from './CardNumberInput';
import { PAYMENT_CONSTANTS } from './constants';
import {
  getCountryFromCurrency,
  getFieldErrorMessage,
  isCanadianUser,
  validateStripeElements,
  validateZipOrPostalCode,
} from './paymentValidationHelpers';
import PromoCodeSection, { PromoCodeFormData } from './PromoCodeSection';
import { createStripePaymentMethod } from './stripePaymentHelpers';
import StripeProvider from './StripeProvider';

export interface PaymentCardFormData extends PromoCodeFormData {
  zipCode: string;
}

export interface PaymentFees {
  applicationFee: number;
  licenseTransferFees?: Array<{
    state: string; // State code (e.g., 'CA', 'TX')
    stateName: string; // Full state name (e.g., 'California', 'Texas')
    amount: number;
  }>;
  otherFees?: Array<{ label: string; amount: number }>;
  currency?: string; // Currency code (e.g., 'USD')
}

interface PaymentCardFormProps {
  fees: PaymentFees;
  applicationId: string;
  onSubmit?: (
    paymentMethodId: string,
    data: PaymentCardFormData,
  ) => Promise<void>;
  onSkipPayment?: () => Promise<void>;
  className?: string;
  invitationId?: string;
  invitationType?: InvitationType;
}

const PaymentCardFormContent = forwardRef<
  any,
  {
    control: Control<PaymentCardFormData>;
    errors: FieldErrors<PaymentCardFormData>;
    trigger: UseFormTrigger<PaymentCardFormData>;
    fees: PaymentFees;
    applicationId: string;
    onSubmit?: (
      paymentMethodId: string,
      data: PaymentCardFormData,
    ) => Promise<void>;
    onSkipPayment?: () => Promise<void>;
    invitationId?: string;
    invitationType?: InvitationType;
  }
>(
  (
    {
      control,
      errors,
      trigger,
      fees,
      applicationId,
      onSubmit,
      onSkipPayment,
      invitationId,
      invitationType,
    },
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch<AppDispatch>();

    const { promoDiscountInfo, isApplyingPromo, isInvitationDiscount } =
      useSelector((state: RootState) => state.payment);

    // Determine country from currency (more reliable than user account country)
    const userCountry = useMemo(
      () => getCountryFromCurrency(fees.currency),
      [fees.currency],
    );

    const [cardNumberError, setCardNumberError] = useState<string>();
    const [cardExpiryError, setCardExpiryError] = useState<string>();
    const [cardCvcError, setCardCvcError] = useState<string>();
    const [submitError, setSubmitError] = useState<string>();

    // Track completion status of Stripe elements
    const [cardNumberComplete, setCardNumberComplete] = useState(false);
    const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
    const [cardCvcComplete, setCardCvcComplete] = useState(false);

    const zipCode = useWatch({
      control,
      name: 'zipCode',
      defaultValue: '',
    });

    useEffect(() => {
      if (invitationId && invitationType) {
        // Invitation coupon applies only to application fee, not license transfer or other fees
        dispatch(
          checkAndApplyInvitationCoupon(
            applicationId,
            invitationId,
            invitationType,
            fees.applicationFee,
          ),
        );
      }
    }, [dispatch, applicationId, invitationId, invitationType, fees]);

    const feeItems = useMemo<FeeItem[]>(() => {
      const items: FeeItem[] = [
        {
          label: 'Real Brokerage Application Fee',
          amount: fees.applicationFee,
          currency: fees.currency,
        },
      ];

      if (fees.licenseTransferFees && fees.licenseTransferFees.length > 0) {
        fees.licenseTransferFees.forEach((stateFee) => {
          items.push({
            label: `${stateFee.stateName} License Transfer Fees`,
            amount: stateFee.amount,
            currency: fees.currency,
          });
        });
      }

      if (fees.otherFees) {
        fees.otherFees.forEach((fee) => {
          items.push({
            label: fee.label,
            amount: fee.amount,
            currency: fees.currency,
          });
        });
      }

      if (
        promoDiscountInfo &&
        promoDiscountInfo.calculatedDiscount &&
        promoDiscountInfo.calculatedDiscount > 0
      ) {
        items.push({
          label: `Promo Code Applied (${promoDiscountInfo.couponCode})`,
          amount: -promoDiscountInfo.calculatedDiscount,
          isDiscount: true,
          currency: fees.currency,
        });
      }

      return items;
    }, [fees, promoDiscountInfo]);

    const subtotal = useMemo(() => {
      return feeItems.reduce((sum, item) => sum + item.amount, 0);
    }, [feeItems]);

    const handleApplyPromoCode = async (code: string) => {
      // Promo code applies only to application fee, not license transfer or other fees
      await dispatch(applyPromoCode(applicationId, code, fees.applicationFee));
    };

    const handleRemovePromoCode = () => {
      dispatch(clearPromoCode());
    };

    const isPaymentRequired = subtotal >= PAYMENT_CONSTANTS.MIN_PAYMENT_AMOUNT;

    // Validate form fields - call this BEFORE createPaymentMethod
    const validateForm = async (): Promise<boolean> => {
      // No validation needed if payment not required
      if (!isPaymentRequired) {
        return true;
      }

      const validationErrors: string[] = [];

      // Clear previous error states
      setCardNumberError(undefined);
      setCardExpiryError(undefined);
      setCardCvcError(undefined);

      // Validate Stripe elements using helper
      const stripeValidation = validateStripeElements({
        cardNumberComplete,
        cardExpiryComplete,
        cardCvcComplete,
      });

      if (!stripeValidation.isValid) {
        validationErrors.push(...stripeValidation.errors);

        // Set individual field errors
        setCardNumberError(
          getFieldErrorMessage('cardNumber', cardNumberComplete),
        );
        setCardExpiryError(
          getFieldErrorMessage('cardExpiry', cardExpiryComplete),
        );
        setCardCvcError(getFieldErrorMessage('cardCvc', cardCvcComplete));
      }

      // Trigger React Hook Form validation for zip code
      const isZipCodeValid = await trigger('zipCode');
      const isZipCodeEmpty = !zipCode || zipCode.trim() === '';

      if (!isZipCodeValid || isZipCodeEmpty) {
        const errorMessage = errors.zipCode?.message || 'Zip code is required';
        validationErrors.push(errorMessage);
      }

      // Return validation result - errors shown inline on individual fields
      if (validationErrors.length > 0) {
        return false; // Validation failed
      }

      return true; // Validation passed
    };

    // Create payment method to be used by parent component
    // NOTE: This assumes validateForm() was called first and passed
    const createPaymentMethod = async () => {
      setSubmitError(undefined);

      try {
        if (!isPaymentRequired) {
          // No payment required, return empty payment method
          if (onSkipPayment) {
            await onSkipPayment();
          }
          return { paymentMethodId: '', skipped: true };
        }

        // Use helper to create payment method
        const result = await createStripePaymentMethod(
          stripe!,
          elements!,
          zipCode,
        );

        if (result.error) {
          setSubmitError(result.error);
          throw new Error(result.error);
        }

        // Call parent's onSubmit with payment data
        if (onSubmit) {
          await onSubmit(result.paymentMethodId, {
            promoCode: promoDiscountInfo?.couponCode || '',
            zipCode,
          });
        }

        return {
          paymentMethodId: result.paymentMethodId,
          promoCode: promoDiscountInfo?.couponCode || '',
          zipCode,
        };
      } catch (error) {
        const errorMsg =
          error instanceof Error
            ? error.message
            : 'An error occurred processing your request';
        setSubmitError(errorMsg);
        throw error;
      }
    };

    const isFormDisabled = isPaymentRequired && (!stripe || !elements);

    // Expose validateForm and createPaymentMethod to parent
    useImperativeHandle(ref, () => ({
      validateForm,
      createPaymentMethod,
    }));

    return (
      <div className='rounded-[18px] border border-real-blue-200 bg-real-blue-50 p-4 space-y-4'>
        <OneTimeFeeCard items={feeItems} subtotal={subtotal} />

        <PromoCodeSection
          control={control}
          errors={errors}
          onApplyPromoCode={handleApplyPromoCode}
          onRemovePromoCode={handleRemovePromoCode}
          isSubmitting={isApplyingPromo}
          appliedPromoCode={promoDiscountInfo?.couponCode}
          promoDiscount={promoDiscountInfo?.calculatedDiscount}
          isInvitationDiscount={isInvitationDiscount}
        />

        {isPaymentRequired ? (
          <div className='space-y-4'>
            <CardNumberInput
              disabled={isFormDisabled}
              error={cardNumberError}
              onError={setCardNumberError}
              onComplete={setCardNumberComplete}
              label='Card Number'
            />

            <div className='grid grid-cols-2 gap-4'>
              <CardCvcInput
                disabled={isFormDisabled}
                error={cardCvcError}
                onError={setCardCvcError}
                onComplete={setCardCvcComplete}
                label='Security Code'
              />
              <CardExpiryInput
                disabled={isFormDisabled}
                error={cardExpiryError}
                onError={setCardExpiryError}
                onComplete={setCardExpiryComplete}
                label='Expiry Date'
              />
            </div>

            <HookFormTextInput
              control={control}
              name='zipCode'
              label={
                isCanadianUser(userCountry)
                  ? 'Billing Postal Code'
                  : 'Billing Zip Code'
              }
              placeholder={
                isCanadianUser(userCountry) ? 'e.g., A1A 1A1' : 'e.g., 12345'
              }
              disabled={isFormDisabled}
              rules={{
                required: isCanadianUser(userCountry)
                  ? 'Postal code is required'
                  : 'Zip code is required',
                validate: (value: string) =>
                  validateZipOrPostalCode(value, userCountry),
              }}
            />
          </div>
        ) : (
          <div className='text-center py-4'>
            <p className='text-lg text-real-grey-700 font-inter font-medium mb-2'>
              No payment required
            </p>
            <p className='text-sm text-real-grey-600 font-inter'>
              Your fees have been waived or discounted below the minimum payment
              threshold.
            </p>
          </div>
        )}

        {submitError && (
          <div className='rounded-md bg-real-red-50 p-4'>
            <p className='font-inter text-sm text-real-red-600'>
              {submitError}
            </p>
          </div>
        )}
      </div>
    );
  },
);

const PaymentCardForm = forwardRef<any, PaymentCardFormProps>(
  (
    {
      fees,
      applicationId,
      onSubmit,
      onSkipPayment,
      className = '',
      invitationId,
      invitationType,
    },
    ref,
  ) => {
    const {
      control,
      formState: { errors },
      trigger,
    } = useForm<PaymentCardFormData>({
      mode: 'onChange',
      defaultValues: {
        promoCode: '',
        zipCode: '',
      },
    });

    const contentRef = React.useRef<any>(null);

    useImperativeHandle(ref, () => ({
      validateForm: async () => {
        if (contentRef.current) {
          return contentRef.current.validateForm();
        }
        throw new Error('Payment form not initialized');
      },
      createPaymentMethod: async () => {
        if (contentRef.current) {
          return contentRef.current.createPaymentMethod();
        }
        throw new Error('Payment form not initialized');
      },
    }));

    return (
      <div className={className}>
        <StripeProvider>
          <PaymentCardFormContent
            ref={contentRef}
            control={control}
            errors={errors}
            trigger={trigger}
            fees={fees}
            applicationId={applicationId}
            onSubmit={onSubmit}
            onSkipPayment={onSkipPayment}
            invitationId={invitationId}
            invitationType={invitationType}
          />
        </StripeProvider>
      </div>
    );
  },
);

export default PaymentCardForm;
